import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DatePicker,
  Divider,
  Input,
  Layout,
  Modal,
  Select,
  Table,
  message,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import PdButton from 'components/pd-button';
import PromoService from 'services/promos';
import { errHandling, formatDate } from 'utils';
import { capitalize, get } from 'lodash';
import PdCsvDownloader from 'components/pd-csv-downloader';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
import useDebounce from 'hooks/useDebounce';

const { Content } = Layout;
const { confirm } = Modal;

const ALL_PROMO_REPORT_COLUMN = [
  {
    id: 'promo_name',
    displayName: 'Promo Name',
  },
  {
    id: 'voucher_code',
    displayName: 'Voucher Code',
  },
  {
    id: 'start_date',
    displayName: 'Start Date',
  },
  {
    id: 'end_date',
    displayName: 'End Date',
  },
  {
    id: 'quota',
    displayName: 'Quota',
  },
  {
    id: 'promo_used',
    displayName: 'Promo Used',
  },
  {
    id: 'on_hold',
    displayName: 'On Hold',
  },
];

const bulkMenu = [
  {
    label: 'Bulk Delete',
    value: 'deleted',
  },
  {
    label: 'Bulk Reactive',
    value: 'active',
  },
  {
    label: 'Bulk Deactive',
    value: 'inactive',
  },
];

export const Promos = () => {
  const navigate = useNavigate();
  const [keyword, setKeyword] = React.useState('');
  const [promos, setPromos] = React.useState([]);
  const [metadata, setMetadata] = React.useState({ page: 1 });
  const [isLoading, setIsLoading] = React.useState(true);
  const [selectedRowKeys, setSelectedRowKeys] = React.useState([]);
  const [selectedStatus, setSelectedStatus] = React.useState('active');
  const [openModalReactive, setOpenModalReactive] = React.useState(-1);
  const [dateRange, setDateRange] = React.useState([]);

  const debounceKeyword = useDebounce(keyword);

  const getPromos = async (page = 1, status) => {
    try {
      const { data } = await PromoService.getPromos(
        page,
        debounceKeyword,
        10,
        status
      );
      setPromos(data.data);
      setMetadata((prev) => ({
        ...prev,
        ...data.metadata,
      }));
    } catch (error) {
      errHandling(error);
    } finally {
      setIsLoading(false);
    }
  };

  async function handleRestore(id, data) {
    try {
      if (id) {
        await PromoService.restorePromo(id, data);
        message.success('Successfully restored promo');
      }
    } catch (err) {
      const errMessage = get(err, 'response.data.message', '');
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      getPromos(metadata.page, selectedStatus);
      setOpenModalReactive(-1);
    }
  }

  async function hidePromo(id) {
    try {
      if (id) {
        await PromoService.hidePromo(id);
        message.success('successfully hide promo');
      }
    } catch (err) {
      const errMessage = get(err, 'response.data.message', '');
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      getPromos(metadata.page, selectedStatus);
    }
  }

  async function deletePromo(id) {
    try {
      if (id) {
        await PromoService.deletePromo(id);
        message.success('Successfully removed promo');
      }
    } catch (err) {
      const errMessage = get(err, 'response.data.message', '');
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      getPromos(metadata.page, selectedStatus);
    }
  }

  function handleHide(id) {
    confirm({
      title: 'Are you sure you want to hide this promo?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        hidePromo(id);
      },
      onCancel() {},
    });
  }

  function handleDelete(id) {
    confirm({
      title: 'Are you sure you want to delete?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        deletePromo(id);
      },
      onCancel() {},
    });
  }

  async function handleDownloadCsv(id) {
    const data = await PromoService.getPromoReport(id);

    const {
      code,
      end_date,
      promo_codes = [],
      quota,
      start_date,
      title,
      used = 0,
      value,
    } = get(data, 'data.data', {});

    const [minValue, maxValue] = (value || '').split(' - ');

    let reportData = [
      {
        cell_1: 'Voucher Name',
        cell_2: title,
      },
      {
        cell_1: 'Code',
        cell_2: code,
      },
      {
        cell_1: 'Quota',
        cell_2: quota,
      },
      {
        cell_1: 'Used',
        cell_2: used,
      },
      {
        cell_1: 'Value',
        cell_2: maxValue
          ? `${Number(minValue)} - ${Number(maxValue)}`
          : Number(minValue),
      },
      {
        cell_1: 'Start Date',
        cell_2: start_date ? dayjs(start_date).format('DD/MM/YYYY') : undefined,
      },
      {
        cell_1: 'End Date',
        cell_2: end_date ? dayjs(end_date).format('DD/MM/YYYY') : undefined,
      },
      {}, // Expected space between info and row header
      {
        cell_1: 'Code',
        cell_2: 'Order Number',
        cell_3: 'Order Date',
        cell_4: 'Order Amount',
        cell_5: 'Discount',
        cell_6: 'Delivery Fee',
        cell_7: 'Delivery Fee Discount',
        cell_8: 'Total payment',
      },
    ];

    if (quota < 0) {
      reportData.splice(2, 1);
    }

    if (!code) {
      reportData.splice(1, 1);
    }

    if (promo_codes.length > 0) {
      let promoList = [];
      promo_codes.forEach((promo_code = {}) => {
        const promoRows = (promo_code.order_data || []).map((data = {}) => ({
          cell_1: promo_code.code,
          cell_2: data.order_number,
          cell_3: data.order_date
            ? dayjs(data.order_date).format('DD/MM/YYYY HH:mm:ss')
            : undefined,
          cell_4: Number(data.order_amount || 0),
          cell_5: Number(data.discount || 0),
          cell_6: Number(data.delivery_fee || 0),
          cell_7: Number(data.delivery_fee_discount || 0),
          cell_8: Number(data.total_payment || 0),
        }));
        promoList = [...promoList, ...promoRows];
      });
      reportData = [...reportData, ...promoList];
    }

    return reportData
  }

  async function bulkDelete() {
    try {
      const payload = selectedRowKeys.map((row) => ({
        id: row.id,
      }));
      await PromoService.bulkDelete(payload);
      message.success('Successfully removed promo');
      setSelectedRowKeys([]);
    } catch (err) {
      const errMessage = get(err, 'response.data.message', '');
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      getPromos(metadata.page, selectedStatus);
    }
  }

  async function bulkChangeStatusPromo(status, payload) {
    try {
      if (status === 'active') {
        await PromoService.bulkChangeStatus(
          status,
          selectedRowKeys.map((row) => row.id).toString(),
          payload
        );
      } else {
        await PromoService.bulkChangeStatus(
          status,
          selectedRowKeys.map((row) => row.id).toString()
        );
      }

      message.success('Successfully change status');
      setSelectedRowKeys([]);
      setOpenModalReactive(-1);
    } catch (err) {
      const errMessage = get(err, 'response.data.message', '');
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      getPromos(metadata.page, selectedStatus);
    }
  }

  async function handleDownloadAllPromoCsv() {
    const res = await PromoService.getAllPromoReport(
      selectedStatus,
      selectedRowKeys.map((d) => d.id).toString()
    );
    const data = get(res, 'data.data', []);

    const reportData = data.map((promo) => ({
      promo_name: `"${promo.promo_name || ''}"`,
      voucher_code:
        promo.voucher_code && promo.voucher_code.length === 1
          ? `"${(promo.voucher_code || '').toString()}"`
          : undefined,
      start_date: promo.start_date
        ? dayjs(promo.start_date).format('DD/MM/YYYY HH:mm:ss')
        : undefined,
      end_date: promo.end_date
        ? dayjs(promo.end_date).format('DD/MM/YYYY HH:mm:ss')
        : undefined,
      quota: promo.quota && promo.quota > -1 ? promo.quota : 'unlimited',
      promo_used: promo.promo_used > -1 ? promo.promo_used : undefined,
      on_hold: promo.on_hold,
    }));

    return reportData
  }

  const handleTableChange = (pagination) => {
    setMetadata((prev) => ({
      ...prev,
      page: pagination.current,
    }));
    getPromos(pagination.current, selectedStatus);
  };

  const status = {
    active: 'Active',
    inactive: 'Inactive',
    expired: 'Expired',
  };

  const rowSelection = {
    onChange: (_selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRows);
    },
    getCheckboxProps: (record) => ({
      name: record.title,
    }),
    selectedRowKeys: selectedRowKeys.map((d) => d.id),
  };

  const columns = [
    {
      title: 'Image',
      dataIndex: 'mobile_image',
      key: 'mobile_image',
      render: (value) => (
        <img
          src={value ? value : `/img/logo/promo-placehold.png`}
          alt="promo"
          style={{
            objectFit: 'contain',
            width: 140,
            height: 45,
          }}
        />
      ),
    },
    {
      title: 'Period',
      dataIndex: 'start_date',
      key: 'start_date',
      render: (value, record) => (
        <>
          <div>{get(record, 'code', '')}</div>
          <div>{`${value ? formatDate(value || '') :"~"} - ${record.end_date ? formatDate(
            record.end_date
          ):"~"}`}</div>
        </>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value) => <div className={value}>{status[value]}</div>,
    },
    {
      title: 'Action',
      dataIndex: 'id',
      key: 'id',
      align: 'center',
      width: 350,
      render: (value, record) => (
        <div
          style={{
            display: 'flex',
            gap: 16,
            alignItems: 'center',
          }}>
          <PdCsvDownloader
            datas={() => handleDownloadCsv(value)}
            filename="homestory-voucher-report"
            noHeader={true}
          />
          {record.status === 'active' && (
            <PdButton type="danger" outline onClick={() => handleHide(value)}>
              Deactive
            </PdButton>
          )}
          {['expired', 'active'].includes(record.status) && (
            <PdButton
              type="primary"
              onClick={() => navigate(`/promos/edit?id=${value}`)}>
              Edit
            </PdButton>
          )}
          {record.status === 'inactive' && (
            <PdButton
              type="primary"
              outline
              onClick={() => {
                setDateRange([]);
                setOpenModalReactive(value);
              }}>
              Reactive
            </PdButton>
          )}
          {['inactive', 'expired'].includes(record.status) && (
            <PdButton type="danger" onClick={() => handleDelete(value)}>
              Delete
            </PdButton>
          )}
        </div>
      ),
    },
  ];

  const onActionBulk = (value) => {
    if (value === 'deleted') return bulkDelete();
    if (value === 'active') {
      setDateRange([]);
      return setOpenModalReactive('bulk');
    }
    return bulkChangeStatusPromo(value);
  };

  React.useEffect(() => {
    getPromos(metadata.page, selectedStatus);
    //eslint-disable-next-line
  }, [debounceKeyword]);

  return (
    <Layout className="pd-cms-promo main-container">
      <div className="container">
        <div className="content">
          <div className="pageTitle">Promos</div>
          <Content className="pageContent">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
              }}>
              <Input
                style={{
                  width: 300,
                }}
                placeholder="Search Promo List"
                onChange={(e) => setKeyword(e.target.value)}
                prefix={<SearchOutlined />}
              />
              <PdButton
                type="primary"
                className="button-list"
                onClick={() => navigate('/promos/create')}>
                Add New Promo
              </PdButton>
            </div>
            <Divider />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                marginBottom: 32,
              }}>
              <Select
                style={{ width: 200 }}
                placeholder="Select status"
                onChange={(value) => {
                  if (value === 'All Status') {
                    getPromos(1, '');
                    setSelectedStatus('');
                  } else {
                    setSelectedStatus(value);
                    getPromos(1, value);
                  }
                }}>
                {['active', 'inactive', 'expired', 'All Status'].map(
                  (status) => (
                    <Select.Option value={status} key={status}>
                      {capitalize(status)}
                    </Select.Option>
                  )
                )}
              </Select>
              <PdCsvDownloader
                columns={ALL_PROMO_REPORT_COLUMN}
                datas={handleDownloadAllPromoCsv}
                filename="homestory-all-voucher-report"

              />
              {selectedRowKeys.length > 0 && (
                <Select
                  style={{ width: 200 }}
                  placeholder="Bulk Menu"
                  onChange={onActionBulk}
                  value={undefined}>
                  {bulkMenu.map((menu) => (
                    <Select.Option
                      value={menu.value}
                      key={menu.value}
                      disabled={
                        menu.value === 'deleted' &&
                        selectedRowKeys.findIndex(
                          (row) => row.status === 'active'
                        ) !== -1
                      }>
                      {menu.label}
                    </Select.Option>
                  ))}
                </Select>
              )}
            </div>

            <Table
              dataSource={promos}
              columns={columns}
              rowKey={(record) => record.id}
              loading={isLoading}
              onChange={handleTableChange}
              pagination={{
                total: metadata.total_items,
                current: Number(metadata.page),
              }}
              scroll={{ x: 1000 }}
              rowSelection={rowSelection}
            />
          </Content>
        </div>
      </div>

      <Modal
        title="Reactive Promo"
        open={openModalReactive !== -1 || openModalReactive === 'bulk'}
        onCancel={() => setOpenModalReactive(-1)}
        width={400}
        footer={null}>
        <p
          style={{
            color: '#4C4F54',
            textAlign: 'center',
            fontWeight: 400,
            width: 250,
            margin: '0px auto',
          }}>
          Your promo has expired, kindly choose a new promo date.
        </p>
        <p
          style={{
            color: '#4C4F54',
            fontWeight: 400,
            marginTop: 16,
          }}>
          Set Range Date
        </p>
        <DatePicker.RangePicker
          showTime
          onChange={(_value, dateString) => setDateRange(dateString)}
          disabledDate={(current) =>
            current && current < dayjs().startOf('day')
          }
        />

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 32,
          }}>
          <PdButton
            style={{
              minWidth: 120,
            }}
            onClick={() => setOpenModalReactive(-1)}
            type="primary"
            outline>
            Cancel
          </PdButton>
          <PdButton
            style={{
              minWidth: 120,
            }}
            onClick={() => {
              if (!dateRange[0] || !dateRange[1]) {
                message.error('Please select date range');
                return;
              }
              const start_date = new Date(dateRange[0]).toISOString();
              const end_date = new Date(dateRange[1]).toISOString();
              const payload = {
                start_date,
                end_date,
              };
              if (openModalReactive === 'bulk') {
                bulkChangeStatusPromo('active', payload);
              } else {
                handleRestore(openModalReactive, payload);
              }
            }}
            type="primary">
            Reactive
          </PdButton>
        </div>
      </Modal>
    </Layout>
  );
};

export default { Promos };
